import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Login.css";
import logo from "../../assets/logo.png";

// Plain-language messages. Wrong email and wrong password deliberately share one
// message so the page never reveals which emails have accounts.
const getFriendlyError = (err) => {
  switch (err?.code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
      return "Incorrect email or password. Please try again.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please wait a few minutes and try again.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact your administrator.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return "We couldn't sign you in. Please try again.";
  }
};

const APP_URL = "https://app.loungeatlas.com";

function Login() {
  const { login, currentUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError("");
    setIsSubmitting(true);
    try {
      await login(email.trim(), password);
      // wait for onAuthStateChanged to trigger and set currentUser
    } catch (err) {
      console.error(err);
      setError(getFriendlyError(err));
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (currentUser && isSubmitting) {
      navigate("/home");
    }
  }, [currentUser, isSubmitting, navigate]);

  const handleCapsLock = (e) => {
    if (e.getModifierState) setCapsLockOn(e.getModifierState("CapsLock"));
  };

  return (
    <div className="login-container">
      <main className="login-card">
        <a
          href={APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="login-logo-link"
          title="Visit Lounge Atlas"
        >
          <img src={logo} alt="Lounge Atlas logo" className="login-logo" />
        </a>

        <h1 className="login-title">Lounge Atlas Portal</h1>
        <p className="login-subtitle">Sign in to your admin account</p>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-alert" role="alert">
              {error}
            </div>
          )}

          <div className="login-field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              value={email}
              required
              autoFocus
              disabled={isSubmitting}
              aria-invalid={!!error}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="login-field">
            <label htmlFor="login-password">Password</label>
            <div className="login-password-wrap">
              <input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                required
                disabled={isSubmitting}
                aria-invalid={!!error}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={handleCapsLock}
                onKeyDown={handleCapsLock}
                onBlur={() => setCapsLockOn(false)}
              />
              <button
                type="button"
                className="login-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                tabIndex={0}
              >
                {showPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
              </button>
            </div>
            {capsLockOn && <p className="login-hint">Caps Lock is on.</p>}
          </div>

          <button type="submit" className="login-btn" disabled={isSubmitting}>
            {isSubmitting && <span className="login-spinner" aria-hidden="true" />}
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="login-footer">
          Access is restricted to authorized Lounge Atlas staff.
        </p>
      </main>

      <p className="login-copyright">
        © {new Date().getFullYear()} Lounge Atlas ·{" "}
        <a href={APP_URL} target="_blank" rel="noopener noreferrer">
          app.loungeatlas.com
        </a>
      </p>
    </div>
  );
}

export default Login;